import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import { CONTAINER, EYEBROW, NAMESPACE, Rich, SECTION, SECTION_BAND, SECTION_LEAD, SECTION_TITLE } from '../components/ui';

const ITEMS = ['vpn', 'safe', 'games', 'provider', 'free', 'fps'];

const DETAILS = 'faq__i group/faq overflow-hidden rounded-[1rem] border border-white/10 bg-[linear-gradient(180deg,rgb(255_255_255/.05),rgb(255_255_255/.012))] [transition:border-color_var(--dur-base)_var(--ease-out),background_var(--dur-base)_var(--ease-out)] open:border-[#93FF18]/[.34] open:bg-[linear-gradient(180deg,rgb(147_255_24/.07),rgb(255_255_255/.012))] forced-colors:border-[CanvasText] forced-colors:bg-[Canvas]';
const SUMMARY = 'relative flex min-h-[3.25rem] cursor-pointer list-none items-center justify-between gap-4 px-[clamp(1rem,.85rem+.6vw,1.35rem)] py-[clamp(.9rem,.75rem+.5vw,1.15rem)] text-[1rem] font-semibold leading-[1.35] tracking-[-.012em] text-white after:pointer-events-none after:absolute after:inset-[3px] after:z-[2] after:rounded-[calc(var(--r-lg)-3px)] after:opacity-0 after:shadow-[inset_0_0_0_2px_rgb(4_8_6/.92),inset_0_0_0_5px_var(--fg-strong)] focus-visible:shadow-none focus-visible:outline-none focus-visible:after:opacity-100 forced-colors:after:hidden forced-colors:focus-visible:outline forced-colors:focus-visible:outline-[3px] forced-colors:focus-visible:-outline-offset-[3px] forced-colors:focus-visible:outline-[Highlight] [&::-webkit-details-marker]:hidden [@media(pointer:coarse)]:min-h-14';
/* the +/− mark: two centred bars (inset-0 + m-auto keeps them centred in either writing direction) */
const MARK = 'relative size-6 flex-none rounded-full border border-[#424A5B] bg-white/5 [transition:transform_var(--dur-base)_var(--ease-out),border-color_var(--dur-base)_var(--ease-out),background_var(--dur-base)_var(--ease-out)] before:absolute before:inset-0 before:m-auto before:h-[1.5px] before:w-[11px] before:rounded-[1px] before:bg-current before:text-[#CBD5E1] after:absolute after:inset-0 after:m-auto after:h-[11px] after:w-[1.5px] after:rounded-[1px] after:bg-current after:text-[#CBD5E1] after:[transition:opacity_var(--dur-fast)_var(--ease-out)] group-open/faq:border-[#93FF18]/50 group-open/faq:bg-[#93FF18]/[.14] group-open/faq:text-[#93FF18] group-open/faq:[transform:rotate(180deg)] group-open/faq:before:bg-[#93FF18] group-open/faq:after:opacity-0';
const ANSWER = 'px-[clamp(1rem,.85rem+.6vw,1.35rem)] pb-[clamp(1rem,.85rem+.6vw,1.35rem)] pt-0 [&_b]:font-semibold [&_b]:text-[#CBD5E1] [&_p]:max-w-[40em] [&_p]:text-[.875rem] [&_p]:leading-[1.65] [&_p]:text-[#9AA3B5]';

export function Faq({ onCta }: { onCta: () => void }) {
  const { t } = useTranslation(NAMESPACE);
  // one open at a time (what <details name> does where it is supported)
  const [openItem, setOpenItem] = useState<string | null>(ITEMS[0] ?? null);

  return (
    <section className={`${SECTION} ${SECTION_BAND}`} id='faq' aria-labelledby='faq-title'>
      <div className='pointer-events-none absolute inset-0 z-[-1] overflow-hidden' aria-hidden='true'>
        <span className='sec__grid' />
      </div>
      <div className={CONTAINER}>
        <div className='grid items-start gap-[clamp(1.5rem,1rem+2.4vw,3rem)] [@media(width>=60rem)]:grid-cols-[minmax(0,21rem)_minmax(0,1fr)] [@media(width>=60rem)]:gap-[clamp(2rem,1rem+3.5vw,4.5rem)]'>
          <div className='[@media(width>=60rem)]:sticky [@media(width>=60rem)]:top-[calc(var(--header-h)+2rem)]' data-reveal>
            <p className={EYEBROW}>{t('faq.eyebrow')}</p>
            <h2 className={SECTION_TITLE} id='faq-title'>
              <Rich i18nKey='faq.title' />
            </h2>
            <p className={SECTION_LEAD}>{t('faq.lead')}</p>
            <a
              className='btn btn-primary mt-6 !max-w-full !whitespace-normal !px-[clamp(1rem,.5rem+2.2vw,2.5rem)] !py-[.6rem] !text-center !leading-tight !tracking-[.05em]'
              href='#np-form'
              onClick={(event) => {
                event.preventDefault();
                onCta();
              }}
            >
              {t('common.cta')}
            </a>
          </div>

          <div className='grid min-w-0 gap-[.6rem]' data-reveal>
            {ITEMS.map((item) => (
              <details
                key={item}
                className={DETAILS}
                open={openItem === item}
                onToggle={(event) => {
                  const isOpen = event.currentTarget.open;
                  setOpenItem((current) => {
                    if (isOpen) return item;
                    return current === item ? null : current;
                  });
                }}
              >
                <summary className={SUMMARY}>
                  <span className='min-w-0 [@media(width<22rem)]:[overflow-wrap:anywhere]'>{t(`faq.items.${item}.q`)}</span>
                  <i className={MARK} aria-hidden='true' />
                </summary>
                <div className={ANSWER}>
                  <p>
                    <Rich i18nKey={`faq.items.${item}.a`} />
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
